// Service Types admin — manage the picklist used on Enquiries & Quotes.
// Director/administrator only. Deleting a type only removes it from the
// picker; enquiries already using it keep the value (no data loss).
import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Save, Loader2, Tags } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ADMIN_ROLES = new Set(["director", "administrator"]);
const inputCls = "w-full h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm focus:outline-none focus:border-[var(--brand-accent,#C75A1B)]";

export default function ServiceTypesPage() {
    const { user } = useAuth();
    const [types, setTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialog, setDialog] = useState(null); // { mode: create|edit, t? }
    const isAdmin = user && ADMIN_ROLES.has(user.role);

    function load() {
        setLoading(true);
        api.get("/quote-service-types").then((r) => setTypes(r.data))
            .catch(() => toast.error("Could not load service types"))
            .finally(() => setLoading(false));
    }
    useEffect(() => { if (isAdmin) load(); else setLoading(false); }, [isAdmin]);

    async function remove(t) {
        if (!window.confirm(`Remove "${t.name}" from the Service Type picklist? Enquiries already using it are unaffected.`)) return;
        try { await api.delete(`/quote-service-types/${t.id}`); toast.success("Removed"); load(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Delete failed"); }
    }

    if (!isAdmin) return <div data-testid="service-types-page" className="py-12 text-center text-[#9A968F]">Admin-only section.</div>;

    return (
        <div data-testid="service-types-page" className="space-y-5">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <span className="inline-block pb-1 text-[10.5px] font-bold tracking-[.08em] uppercase" style={{ borderBottom: "2px solid var(--brand-accent,#C75A1B)", color: "#6F6A64" }}>Admin · Settings</span>
                    <h2 className="text-2xl font-bold tracking-tight mt-2 text-[#1F1F1F]">Service Types</h2>
                    <p className="text-[13px] mt-1 text-[#6F6A64]">The picklist offered on Enquiries &amp; Quotes.</p>
                </div>
                <button data-testid="service-type-new" onClick={() => setDialog({ mode: "create" })}
                    className="inline-flex items-center gap-2 text-sm font-semibold px-3.5 py-2 rounded-lg text-white" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                    <Plus size={15} /> New service type
                </button>
            </div>

            {loading ? (
                <div className="py-16 text-center text-[#9A968F]"><Loader2 className="inline animate-spin mr-2" size={16} />Loading…</div>
            ) : types.length === 0 ? (
                <div className="py-16 text-center text-[#9A968F] border border-dashed rounded-xl" style={{ borderColor: "#E5E0DA" }}>No service types yet. Add your first one.</div>
            ) : (
                <div className="bg-white border rounded-[12px] shadow-brand-sm divide-y" style={{ borderColor: "#E5E0DA" }}>
                    {types.map((t) => (
                        <div key={t.id} data-testid={`service-type-row-${t.id}`} className="flex items-center gap-3 px-4 py-3">
                            <Tags size={15} className="text-[#9F4212] shrink-0" />
                            <div className="flex-1 min-w-0 font-semibold text-[#1F1F1F] text-[13.5px]">{t.name}</div>
                            <button data-testid={`service-type-edit-${t.id}`} onClick={() => setDialog({ mode: "edit", t })} className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-2.5 py-1.5 rounded-md border hover:bg-[#F6F4F0]" style={{ borderColor: "#E5E0DA" }}><Pencil size={12} /> Edit</button>
                            <button data-testid={`service-type-delete-${t.id}`} onClick={() => remove(t)} className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-2.5 py-1.5 rounded-md text-[#B33A2E] hover:bg-[#FBE8E5]"><Trash2 size={12} /></button>
                        </div>
                    ))}
                </div>
            )}

            {dialog && <TypeDialog mode={dialog.mode} initial={dialog.t} onClose={() => setDialog(null)} onSaved={() => { setDialog(null); load(); }} />}
        </div>
    );
}

function TypeDialog({ mode, initial, onClose, onSaved }) {
    const [name, setName] = useState(initial?.name || "");
    const [busy, setBusy] = useState(false);

    async function save() {
        if (!name.trim()) { toast.error("Name is required"); return; }
        setBusy(true);
        try {
            const body = { name: name.trim() };
            if (mode === "create") await api.post("/quote-service-types", body);
            else await api.patch(`/quote-service-types/${initial.id}`, body);
            toast.success(mode === "create" ? "Service type created" : "Service type saved");
            onSaved();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
        setBusy(false);
    }

    return (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4">
            <div data-testid="service-type-dialog" className="bg-white rounded-[14px] shadow-xl w-full max-w-md border" style={{ borderColor: "#E5E0DA" }}>
                <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: "#E5E0DA" }}>
                    <h3 className="text-base font-bold text-[#1F1F1F]">{mode === "create" ? "New service type" : "Edit service type"}</h3>
                    <button data-testid="service-type-dialog-close" onClick={onClose} className="w-9 h-9 rounded-[8px] hover:bg-[#F6F4F0] grid place-items-center"><X size={16} /></button>
                </div>
                <div className="p-5 space-y-4">
                    <label className="text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] block">Name
                        <input data-testid="service-type-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Fire Risk Assessment" className={inputCls + " mt-1 font-normal normal-case"} /></label>
                </div>
                <div className="px-5 py-3 border-t flex items-center justify-end gap-2 bg-[#F5F3F0]" style={{ borderColor: "#E5E0DA" }}>
                    <button onClick={onClose} className="px-3 py-2 text-sm text-[#6F6A64]">Cancel</button>
                    <button data-testid="service-type-save" onClick={save} disabled={busy} className="inline-flex items-center gap-2 text-sm font-semibold px-3.5 py-2 rounded-lg text-white disabled:opacity-60" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                        {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {mode === "create" ? "Create" : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
}
